import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Sparkles, Gamepad2, Star, Rocket } from 'lucide-react';
export function HeroSection() {
  return <section className="relative min-h-screen pt-32 pb-20 overflow-hidden bg-gradient-to-b from-candy-lavender/30 to-white" data-id="element-88">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-89">
        <motion.div animate={{
        y: [0, -20, 0],
        rotate: [0, 5, 0]
      }} transition={{
        duration: 5,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-20 left-[10%] w-24 h-24 rounded-full bg-candy-pink/20 blur-xl" data-id="element-90" />
        <motion.div animate={{
        y: [0, 30, 0],
        rotate: [0, -10, 0]
      }} transition={{
        duration: 7,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-40 right-[15%] w-32 h-32 rounded-full bg-candy-teal/20 blur-xl" data-id="element-91" />
        <motion.div animate={{
        scale: [1, 1.2, 1]
      }} transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute bottom-20 left-[20%] w-40 h-40 rounded-full bg-candy-yellow/20 blur-2xl" data-id="element-92" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-93">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-94">
          {/* Text Content */}
          <div className="flex-1 text-center lg:text-left" data-id="element-95">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-96">
              <span className="inline-block px-4 py-2 rounded-full bg-white border-2 border-candy-purple/20 text-candy-purple font-bold text-sm mb-6 shadow-sm" data-id="element-97">
                ✨ The Safest Playground on the Internet
              </span>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight mb-6 text-candy-dark" data-id="element-98">
                Where Every Game <br data-id="element-99" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-candy-pink via-candy-purple to-candy-teal" data-id="element-100">
                  Sparks Joy
                </span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0 font-medium" data-id="element-101">
                Discover a magical world of curated, safe, and ad-free games
                designed specifically for kids. Fun for them, peace of mind for
                you.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4" data-id="element-102">
                <Button size="lg" icon={Gamepad2} className="w-full sm:w-auto shadow-candy-pink/40 hover:shadow-candy-pink/60" data-id="element-103">
                  Explore Games
                </Button>
                <Button variant="outline" size="lg" className="w-full sm:w-auto border-candy-dark text-candy-dark hover:bg-candy-lavender/20" data-id="element-104">
                  For Parents
                </Button>
              </div>

              <div className="mt-10 flex items-center justify-center lg:justify-start gap-2 text-sm font-bold text-gray-500" data-id="element-105">
                <div className="flex -space-x-2" data-id="element-106">
                  {[1, 2, 3, 4].map(i => <div key={i} className={`w-8 h-8 rounded-full border-2 border-white bg-candy-${['pink', 'teal', 'yellow', 'purple'][i - 1]} flex items-center justify-center text-white text-xs`} data-id="element-107">
                      {['👾', '🦄', '🚀', '⭐'][i - 1]}
                    </div>)}
                </div>
                <span className="ml-2" data-id="element-108">Trusted by 10,000+ happy families</span>
              </div>
            </motion.div>
          </div>

          {/* Hero Illustration */}
          <div className="flex-1 relative w-full max-w-lg lg:max-w-none" data-id="element-109">
            <motion.div initial={{
            scale: 0.8,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative aspect-square" data-id="element-110">
              {/* Main floating card */}
              <div className="absolute inset-0 bg-gradient-to-br from-candy-purple to-candy-pink rounded-[3rem] rotate-3 shadow-2xl shadow-candy-purple/30 flex items-center justify-center overflow-hidden" data-id="element-111">
                <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-112"></div>

                {/* Abstract game UI mockup */}
                <div className="relative w-4/5 h-4/5 bg-white/10 backdrop-blur-md rounded-3xl border border-white/30 p-6 flex flex-col items-center justify-center" data-id="element-113">
                  <motion.div animate={{
                  y: [-10, 10, -10]
                }} transition={{
                  duration: 4,
                  repeat: Infinity
                }} className="w-32 h-32 bg-gradient-to-tr from-candy-yellow to-orange-400 rounded-2xl mb-6 shadow-lg flex items-center justify-center text-6xl" data-id="element-114">
                    🎮
                  </motion.div>
                  <div className="w-3/4 h-4 bg-white/20 rounded-full mb-3" data-id="element-115"></div>
                  <div className="w-1/2 h-4 bg-white/20 rounded-full" data-id="element-116"></div>

                  {/* Floating badges */}
                  <motion.div animate={{
                  x: [0, 10, 0],
                  rotate: [0, 5, 0]
                }} transition={{
                  duration: 3,
                  repeat: Infinity
                }} className="absolute -top-6 -right-6 bg-white text-candy-pink p-3 rounded-2xl shadow-lg font-bold flex items-center gap-2" data-id="element-117">
                    <Star className="fill-candy-yellow text-candy-yellow" data-id="element-118" />
                    Top Rated
                  </motion.div>

                  <motion.div animate={{
                  x: [0, -10, 0],
                  rotate: [0, -5, 0]
                }} transition={{
                  duration: 3.5,
                  repeat: Infinity,
                  delay: 0.5
                }} className="absolute -bottom-6 -left-6 bg-white text-candy-teal p-3 rounded-2xl shadow-lg font-bold flex items-center gap-2" data-id="element-119">
                    <Rocket className="fill-candy-teal" data-id="element-120" />
                    New!
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Wavy Divider */}
      <div className="absolute bottom-0 left-0 right-0 translate-y-1" data-id="element-121">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-122">
          <path d="M0 60L48 65C96 70 192 80 288 81.7C384 83.3 480 76.7 576 65C672 53.3 768 36.7 864 33.3C960 30 1056 40 1152 51.7C1248 63.3 1344 76.7 1392 83.3L1440 90V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0V60Z" fill="white" data-id="element-123" />
        </svg>
      </div>
    </section>;
}